<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12" md="3">
          <v-card variant="outlined">
            <v-card-title class="bg-blue-grey-lighten-4 text-subtitle-1">
              Material Receipt
            </v-card-title>
            <v-card-text class="pt-4">
              <v-autocomplete
                v-model="form.requisition_delivery_master_id"
                :items="deliveryList"
                :item-title="deliveryTitle"
                item-value="id"
                label="Posted Inventory Issue *"
                density="compact"
                variant="outlined"
                clearable
                :disabled="isEdit"
                :menu-props="{ contentClass: 'my-custom-dropdown' }"
                @update:model-value="changeDelivery"
              />

              <v-date-input
                v-model="form.receive_date"
                label="Receive Date *"
                input-format="dd/mm/yyyy"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                density="compact"
                variant="outlined"
              />

              <v-text-field
                :model-value="selectedDelivery?.warehouse?.name || '-'"
                label="Source Warehouse"
                density="compact"
                variant="outlined"
                readonly
              />

              <v-text-field
                :model-value="selectedDelivery?.requisition_master?.code || '-'"
                label="Production Requisition"
                density="compact"
                variant="outlined"
                readonly
              />

              <v-textarea
                v-model="form.remarks"
                label="Remarks"
                density="compact"
                variant="outlined"
                rows="3"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="9">
          <v-card variant="outlined">
            <v-card-title class="bg-blue-grey-lighten-4 text-subtitle-1">
              Received Materials &amp; Lot Traceability
            </v-card-title>
            <v-card-text class="pt-4">
              <v-alert type="info" variant="tonal" density="compact" class="mb-3">
                Receive quantity is entered per material. The backend distributes it against the posted issue lots in order and preserves lot, Purchase Purity and cost snapshots.
              </v-alert>

              <v-table density="compact" class="border-sm">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Material</th>
                    <th>Unit</th>
                    <th class="text-end">Issued</th>
                    <th class="text-end">Already Received</th>
                    <th style="width: 150px">Receive Qty</th>
                    <th>Issued Lots</th>
                    <th style="width: 45px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in cart" :key="item.production_requisition_delivery_detail_id">
                    <td>{{ index + 1 }}</td>
                    <td>
                      <div class="font-weight-medium">{{ item.product_code }} - {{ item.product_name }}</div>
                      <div class="text-caption text-medium-emphasis">
                        Standard purity: {{ number(item.standard_purity, 4) }}%
                      </div>
                    </td>
                    <td>{{ item.unit_name }}</td>
                    <td class="text-end">{{ number(item.issued_quantity) }}</td>
                    <td class="text-end">{{ number(item.received_quantity) }}</td>
                    <td>
                      <v-text-field
                        v-model.number="item.quantity"
                        type="number"
                        min="0.0001"
                        :max="item.pending_quantity"
                        step="0.0001"
                        density="compact"
                        variant="outlined"
                        hide-details
                        @update:model-value="clampQuantity(item)"
                      />
                      <div class="text-caption">Pending: {{ number(item.pending_quantity) }}</div>
                    </td>
                    <td>
                      <div v-for="allocation in item.allocations" :key="allocation.id" class="lot-line">
                        <strong>{{ allocation.lot_no || allocation.batch_no || '-' }}</strong>
                        · purity {{ number(allocation.purchase_purity, 4) }}%
                        · qty {{ number(allocation.quantity) }}
                        · received {{ number(allocation.received_quantity) }}
                        · exp {{ allocation.expiry_date || '-' }}
                      </div>
                    </td>
                    <td>
                      <v-btn
                        icon="mdi-delete-outline"
                        size="x-small"
                        color="error"
                        variant="text"
                        @click="cart.splice(index, 1)"
                      />
                    </td>
                  </tr>
                  <tr v-if="cart.length === 0">
                    <td colspan="8" class="text-center text-medium-emphasis py-6">
                      Select a posted material issue with a pending receipt.
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-card-text>
            <v-divider />
            <v-card-actions class="justify-end pa-4">
              <v-btn variant="tonal" :disabled="saving" @click="submit(0)">Save Draft</v-btn>
              <v-btn color="primary" :loading="saving" @click="submit(1)">Submit for Approval</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";

const store = useStore();
const route = useRoute();
const router = useRouter();

const receiveId = route.params.id || route.query.id || null;
const isEdit = Boolean(receiveId);
const saving = ref(false);
const cart = ref([]);
const deliveryList = ref([]);

const form = reactive({
  requisition_delivery_master_id: null,
  receive_date: new Date(),
  remarks: "",
});

const selectedDelivery = computed(() => deliveryList.value.find(
  (item) => Number(item.id) === Number(form.requisition_delivery_master_id)
) || null);

function number(value, digits = 4) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: digits });
}

function deliveryTitle(item) {
  const requisition = item.requisition_master?.code ? ` · ${item.requisition_master.code}` : "";
  return `${item.code}${requisition} · ${item.requisition_delivery_date || ""}`;
}

function mapDeliveryDetail(item) {
  const issued = Number(item.qty || 0);
  const received = Number(item.receive_qty || 0);
  const pending = Math.max(0, issued - received);
  return {
    product_id: item.product_id,
    production_requisition_delivery_detail_id: item.id,
    product_name: item.product?.name || "",
    product_code: item.product?.code || "",
    unit_name: item.product?.unit?.name || "",
    standard_purity: Number(item.standard_purity || 0),
    issued_quantity: issued,
    received_quantity: received,
    pending_quantity: pending,
    quantity: pending,
    allocations: item.allocations || [],
  };
}

function changeDelivery() {
  const delivery = selectedDelivery.value;
  cart.value = delivery
    ? (delivery.requisition_delivery_details || []).map(mapDeliveryDetail).filter((item) => item.pending_quantity > 0)
    : [];
  if (delivery?.requisition_delivery_date && new Date(form.receive_date) < new Date(delivery.requisition_delivery_date)) {
    form.receive_date = new Date(delivery.requisition_delivery_date);
  }
}

function clampQuantity(item) {
  item.quantity = Math.min(
    Math.max(0, Number(item.quantity || 0)),
    Number(item.pending_quantity || 0)
  );
}

function validate() {
  if (!form.requisition_delivery_master_id || !form.receive_date) {
    return "Posted Inventory issue and receive date are required.";
  }
  if (!cart.value.length) return "At least one pending material is required.";
  if (cart.value.some((item) => Number(item.quantity || 0) <= 0)) {
    return "Every selected material must have a receive quantity greater than zero.";
  }
  return null;
}

async function submit(status) {
  const validationMessage = validate();
  if (validationMessage) {
    toast.warning(validationMessage);
    return;
  }

  const payload = {
    id: receiveId,
    requisition_delivery_master_id: form.requisition_delivery_master_id,
    receive_date: formattedDate(form.receive_date),
    remarks: form.remarks || null,
    status,
    cart: cart.value.map((item) => ({
      product_id: item.product_id,
      production_requisition_delivery_detail_id: item.production_requisition_delivery_detail_id,
      quantity: Number(item.quantity),
    })),
  };

  saving.value = true;
  try {
    const response = await store.dispatch(
      isEdit ? "updateRawMetarialRequisitionReceive" : "saveRawMetarialRequisitionReceive",
      payload
    );
    toast.success(response?.data?.message || "Production material receipt saved.");
    router.push({ name: "requisition-receive-list" });
  } catch (error) {
    const errors = error?.response?.data?.errors;
    const text = errors ? Object.values(errors).flat().join("\n") : error?.response?.data?.message;
    toast.error(text || "Production material receipt could not be saved.");
  } finally {
    saving.value = false;
  }
}

async function loadEditData(id) {
  const response = await store.dispatch("getRawMetarialRequisitionReceiveById", id);
  const data = response?.data || response;
  const delivery = data.delivery_master;
  if (delivery && !deliveryList.value.some((item) => Number(item.id) === Number(delivery.id))) {
    deliveryList.value.unshift(delivery);
  }
  form.requisition_delivery_master_id = data.requisition_delivery_master_id;
  form.receive_date = data.receive_date ? new Date(data.receive_date) : new Date();
  form.remarks = data.remarks || "";

  const grouped = new Map();
  (data.details || []).forEach((detail) => {
    const key = Number(detail.production_requisition_delivery_detail_id);
    if (!grouped.has(key)) {
      grouped.set(key, {
        product_id: detail.product_id,
        production_requisition_delivery_detail_id: key,
        product_name: detail.product?.name || "",
        product_code: detail.product?.code || "",
        unit_name: detail.product?.unit?.name || "",
        standard_purity: Number(detail.delivery_allocation?.standard_purity || 0),
        issued_quantity: 0,
        received_quantity: 0,
        pending_quantity: 0,
        quantity: 0,
        allocations: [],
      });
    }
    const line = grouped.get(key);
    line.quantity += Number(detail.quantity || 0);
    line.allocations.push({
      id: detail.delivery_allocation_id,
      lot_no: detail.lot_no,
      batch_no: detail.batch_no,
      purchase_purity: detail.purchase_purity,
      quantity: detail.quantity,
      received_quantity: detail.quantity,
      expiry_date: detail.expiry_date,
    });
  });

  const issueDetails = delivery?.requisition_delivery_details || [];
  grouped.forEach((line, key) => {
    const issueLine = issueDetails.find((item) => Number(item.id) === key);
    line.issued_quantity = Number(issueLine?.qty || line.quantity);
    line.received_quantity = Number(issueLine?.receive_qty || 0);
    line.pending_quantity = issueLine
      ? Math.max(0, line.issued_quantity - line.received_quantity)
      : Number(line.quantity);
  });
  cart.value = [...grouped.values()];
}

onMounted(async () => {
  try {
    await store.dispatch("getInvProductionRequisitionDeliveryList", { is_receivable: true });
    deliveryList.value = [...(store.getters.getInvProductionRequisitionDeliveryList || [])];
    if (receiveId) await loadEditData(receiveId);
  } catch (error) {
    toast.error(error?.response?.data?.message || "Receivable Inventory issues could not be loaded.");
  }
});
</script>

<style scoped>
.lot-line {
  border-bottom: 1px dashed rgba(0, 0, 0, 0.15);
  font-size: 0.78rem;
  padding: 3px 0;
}

.lot-line:last-child {
  border-bottom: 0;
}
</style>
